import { __awaiter } from '../../../../../_virtual/_tslib.js';
import { useState, useRef, useMemo } from 'react';
import { useInternalDynamicContext } from '../../../context/DynamicContext/useInternalDynamicContext.js';
import { getOauthLoginUrl, unlinkOAuth, verifyOAuth } from '../../../data/api.js';
import { useUpdateUserProfileByJWTCallback } from '../useUpdateUserProfileByJWTCallback/useUpdateUserProfileByJWTCallback.js';
import '@dynamic-labs/sdk-api';
import { logger } from '../../../shared/logger.js';
import '@dynamic-labs/iconic';
import '@dynamic-labs/wallet-connector-core';
import 'react/jsx-runtime';
import '../../../context/ViewContext/ViewContext.js';
import '@dynamic-labs/wallet-book';
import '@dynamic-labs/utils';
import '../../constants/colors.js';
import '../../../shared/utils/classes/storage/localStorage.js';
import 'viem';
import '../../../shared/consts/index.js';
import '@dynamic-labs/multi-wallet';
import { getAuthToken } from '../../functions/getAuthToken/getAuthToken.js';
import { getEnabledOAuthProviders } from '../../functions/getEnabledOAuthProviders/index.js';
import { isSocialKycEnabled } from '../../functions/isSocialKycEnabled/isSocialKycEnabled.js';
import { randomString } from '../../functions/randomString/randomString.js';
import { stringifyURIQuery } from '../../functions/stringifyURIQuery/stringifyURIQuery.js';
import { digestSHA256 } from '../../functions/digestSHA256/digestSHA256.js';
import { encodeBase64URL } from '../../functions/encodeBase64URL/encodeBase64URL.js';
import { getRedirectURIOrigin } from '../../functions/getRedirectURIOrigin/getRedirectURIOrigin.js';

const socialProviders = [
    'discord',
    'facebook',
    'github',
    'google',
    'instagram',
    'twitch',
    'twitter',
];
// pkce params are only required for twitter and break if added for other providers, like google
// so we should only add provider that support pkce to this list
const providersRequiringPkce = ['twitter', 'github'];
const providersWithoutWindowOpenerRefence = ['twitter'];
const useSocialAccounts = ({ sessionTimeout } = {
    sessionTimeout: 10000,
}) => {
    var _a;
    const { environmentId, user, projectSettings, onboardingOnlyJwt } = useInternalDynamicContext();
    const { updateUserProfileByJWT } = useUpdateUserProfileByJWTCallback();
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState();
    const providersWaitingOauthMessages = useRef({});
    const authWindowInterval = useRef();
    const userJwt = (_a = getAuthToken()) !== null && _a !== void 0 ? _a : onboardingOnlyJwt;
    const oauthProviders = getEnabledOAuthProviders(projectSettings === null || projectSettings === void 0 ? void 0 : projectSettings.providers);
    const verifiedOAuthCredentialsMap = useMemo(() => {
        var _a, _b;
        const verifiedOAuthCredential = (_b = (_a = user === null || user === void 0 ? void 0 : user.verifiedCredentials) === null || _a === void 0 ? void 0 : _a.filter((credential) => credential.format === 'oauth')) !== null && _b !== void 0 ? _b : [];
        return Object.fromEntries(new Map(verifiedOAuthCredential.map((credential) => {
            var _a;
            return [
                (_a = credential.oauthProvider) !== null && _a !== void 0 ? _a : credential.id,
                credential,
            ];
        })));
    }, [user === null || user === void 0 ? void 0 : user.verifiedCredentials]);
    const handleError = (message) => {
        logger.error(message);
        setIsProcessing(false);
        setError(message);
    };
    const addOAuthIFrame = (url) => {
        const container = document.getElementById('oauth-iframe-container');
        let oauthIFrame = document.getElementById('oauth-iframe');
        if (!container || oauthIFrame) {
            return;
        }
        oauthIFrame = document.createElement('iframe');
        oauthIFrame.setAttribute('id', 'oauth-iframe');
        oauthIFrame.setAttribute('src', url);
        container.appendChild(oauthIFrame);
    };
    const removeOAuthIFrame = () => {
        const oauthIFrame = document.getElementById('oauth-iframe');
        if (oauthIFrame) {
            oauthIFrame.remove();
        }
    };
    const checkValidProvider = (provider) => {
        if (!provider) {
            handleError('Provider is required');
            return false;
        }
        if (!socialProviders.includes(provider)) {
            handleError(`Invalid social provider. Valid providers are: ${socialProviders.join(', ')}`);
            return false;
        }
        return true;
    };
    const checkValidOAuthLinking = (provider) => {
        if (!isSocialKycEnabled(projectSettings)) {
            handleError('Social linking is not enabled on the dashboard');
            return false;
        }
        if (!checkValidProvider(provider)) {
            return false;
        }
        const verifiedCredential = verifiedOAuthCredentialsMap[provider];
        if (verifiedCredential) {
            handleError(`Account already linked for provider: ${provider}`);
            return false;
        }
        const oauthProvider = oauthProviders.find((p) => p.provider === provider);
        if (!oauthProvider) {
            handleError(`Provider is not enabled on the dashboard: ${provider}`);
            return false;
        }
        return true;
    };
    const linkSocialAccount = (provider) => __awaiter(void 0, void 0, void 0, function* () {
        var _b;
        clearInterval(authWindowInterval.current);
        setError(undefined);
        setIsProcessing(true);
        // invalid linking request
        if (!checkValidOAuthLinking(provider)) {
            return;
        }
        const state = randomString(8);
        const verifier = randomString(43);
        const clearListeners = () => {
            removeOAuthIFrame();
            window.removeEventListener('message', handleAuthWindowMessage);
            providersWaitingOauthMessages.current = Object.assign(Object.assign({}, providersWaitingOauthMessages.current), { [provider]: false });
        };
        const handleAuthWindowMessage = (event) => __awaiter(void 0, void 0, void 0, function* () {
            var _c;
            const message = event.data;
            const isAuthorizationMessage = (message === null || message === void 0 ? void 0 : message.type) === 'authorization_response';
            if (isAuthorizationMessage) {
                logger.debug('Message received', { data: message });
            }
            const isValidMessage = isAuthorizationMessage && (message === null || message === void 0 ? void 0 : message.provider) === provider;
            // don't process imvalid messages for provider
            if (!isValidMessage) {
                return;
            }
            setIsProcessing(true);
            if (!((_c = providersWaitingOauthMessages.current) === null || _c === void 0 ? void 0 : _c[provider])) {
                handleError(`Linking ${provider} account session timeout.`);
                return;
            }
            clearListeners();
            const { code, error } = message;
            if (error && error !== 'undefined') {
                handleError(`Failed to link ${provider} social account: ${error}`);
                return;
            }
            if (!code) {
                handleError(`Failed to link ${provider} social account: no authorization code`);
                return;
            }
            const jwt = yield verifyOAuth(environmentId, provider, {
                code,
                codeVerifier: verifier,
                state,
            }, String(userJwt));
            if (!jwt) {
                handleError(`Failed to link social account: ${provider}`);
                return;
            }
            yield updateUserProfileByJWT(jwt);
            setIsProcessing(false);
        });
        const loginUrl = yield getOauthLoginUrl(environmentId, provider, String(userJwt));
        if (!loginUrl) {
            handleError(`Failed to get login url to link social account: ${provider}`);
            return;
        }
        // we need to use an iframe to be the intermediary in communications between the popup and the sdk
        // because of twitter limitations with accessing the window.opener and BroadcastChannel not working between
        // different origins (our api redirect page and the app where the sdk is running)
        const oauthIFrameUrl = `${getRedirectURIOrigin(loginUrl)}/sdkoauth`;
        addOAuthIFrame(oauthIFrameUrl);
        let oauthLoginUrl = loginUrl;
        if (providersRequiringPkce.includes(provider)) {
            const digest = yield digestSHA256(verifier);
            const pkceParams = stringifyURIQuery({
                code_challenge: encodeBase64URL(digest),
                code_challenge_method: 'S256',
                state: state,
            });
            oauthLoginUrl = `${loginUrl}&${pkceParams}`;
        }
        if (!((_b = providersWaitingOauthMessages.current) === null || _b === void 0 ? void 0 : _b[provider])) {
            window.addEventListener('message', handleAuthWindowMessage);
            providersWaitingOauthMessages.current = Object.assign(Object.assign({}, providersWaitingOauthMessages.current), { [provider]: true });
        }
        const authWindow = window.open(oauthLoginUrl, '_blank', 'width=500,height=600');
        if (!providersWithoutWindowOpenerRefence.includes(provider)) {
            // For provider that support window.opener, we need to clear all states/listeners when the window is closed
            authWindowInterval.current = setInterval(() => {
                if (!(authWindow === null || authWindow === void 0 ? void 0 : authWindow.closed)) {
                    return;
                }
                clearInterval(authWindowInterval.current);
                setIsProcessing(false);
            });
        }
        else {
            // For provider that don't support window.opener, we need to use a timeout to try to detect when the
            // window is closed
            authWindowInterval.current = setInterval(() => {
                var _a;
                // user didn't complete oauth
                if ((_a = providersWaitingOauthMessages.current) === null || _a === void 0 ? void 0 : _a[provider]) {
                    clearListeners();
                    handleError(`Linking ${provider} account window timeout.`);
                }
                setIsProcessing(false);
                clearInterval(authWindowInterval.current);
            }, sessionTimeout);
        }
    });
    const unlinkSocialAccount = (provider) => __awaiter(void 0, void 0, void 0, function* () {
        var _d;
        setError(undefined);
        setIsProcessing(true);
        if (!checkValidProvider(provider)) {
            return;
        }
        const verifiedCredential = verifiedOAuthCredentialsMap[provider];
        if (!verifiedCredential) {
            handleError(`No account linked for provider: ${provider}`);
            return;
        }
        try {
            const jwt = yield unlinkOAuth({
                environmentId,
                verifiedCrentialId: (_d = verifiedCredential.id) !== null && _d !== void 0 ? _d : '',
            }, String(userJwt));
            if (!jwt) {
                handleError(`Failed to unlink social account: ${provider} | ${verifiedCredential.id}`);
                return;
            }
            yield updateUserProfileByJWT(jwt);
            setIsProcessing(false);
        }
        catch (err) {
            handleError(`Failed to unlink social account: ${provider} | ${verifiedCredential.id}`);
        }
    });
    const isLinked = (provider) => Boolean(verifiedOAuthCredentialsMap[provider]);
    const getLinkedAccountInformation = (provider) => {
        const verifiedCredential = verifiedOAuthCredentialsMap[provider];
        if (!verifiedCredential) {
            return;
        }
        const { id, oauthAccountId, oauthAccountPhotos, oauthDisplayName, oauthEmails, oauthProvider, oauthUsername, publicIdentifier, } = verifiedCredential;
        return {
            accountId: oauthAccountId,
            avatar: oauthAccountPhotos === null || oauthAccountPhotos === void 0 ? void 0 : oauthAccountPhotos[0],
            displayName: oauthDisplayName,
            email: oauthEmails === null || oauthEmails === void 0 ? void 0 : oauthEmails[0],
            id,
            provider: oauthProvider,
            publicIdentifier,
            username: oauthUsername,
        };
    };
    return {
        error,
        getLinkedAccountInformation,
        isLinked,
        isProcessing,
        linkSocialAccount,
        unlinkSocialAccount,
    };
};

export { useSocialAccounts };
